import { useState, useEffect, useMemo } from "react";
import Layout from "@/components/Layout";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Users, 
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Mail,
  Check,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowRightLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function SellersPage() {
  const { user } = useAuth();
  const [sellers, setSellers] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [filterConcession, setFilterConcession] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const itemsPerPage = 15;
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    concession_id: "",
    brands: [],
    brand_distribution: []
  });
  const [saving, setSaving] = useState(false);
  const [allGroupBrands, setAllGroupBrands] = useState([]);

  // Transfer seller between concessions
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState("");
  const [transferMigrate, setTransferMigrate] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [allConcessions, setAllConcessions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterConcession, searchQuery]);

  const fetchData = async () => {
    try {
      const [sellersRes, concessionsRes, brandsRes] = await Promise.all([
        api.get("/sellers"),
        api.get("/concessions"),
        api.get("/brands/all")
      ]);
      setSellers(sellersRes.data);
      setConcessions(concessionsRes.data);
      setAllGroupBrands(brandsRes.data.brands || []);
      
      // Set default concession for manager
      if (user?.role === "manager" && user?.concession_id) {
        setFormData(prev => ({ ...prev, concession_id: user.concession_id }));
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // Check if selected concession is Fleet
  const isFleetConcession = () => {
    const selectedConcession = concessions.find(c => c.id === formData.concession_id);
    return selectedConcession?.is_fleet === true;
  };

  // Get available brands for selected concession
  const getAvailableBrands = () => {
    const selectedConcession = concessions.find(c => c.id === formData.concession_id);
    if (!selectedConcession) return [];

    // For Fleet: return all group brands
    if (selectedConcession.is_fleet) {
      return allGroupBrands;
    }

    // Non-Fleet: prefer the concession's declared brands, but always fall back
    // to the full group brand list if the concession has no brands defined
    // (avoids blocking the manager right after a seller transfer).
    const concessionBrands = (selectedConcession.brands && selectedConcession.brands.length > 0)
      ? selectedConcession.brands
      : (selectedConcession.brand ? [selectedConcession.brand] : []);

    if (concessionBrands.length === 0) {
      return allGroupBrands;
    }

    // Merge: concession brands FIRST, then any extra group brands the user might need
    // (dedupe by uppercase name). This unblocks cases where the concession is
    // missing a brand that must be assignable (e.g. KDE without KIA).
    const seen = new Set(concessionBrands.map(b => (b || "").toUpperCase()));
    const extras = (allGroupBrands || []).filter(b => !seen.has((b || "").toUpperCase()));
    return [...concessionBrands, ...extras];
  };

  // Parse legacy brand format into array
  const parseBrands = (seller) => {
    if (seller.brands && Array.isArray(seller.brands) && seller.brands.length > 0) {
      return seller.brands;
    }
    
    if (!seller.brand) {
      return [];
    }
    
    const brandStr = seller.brand.trim();
    const concession = concessions.find(c => c.id === seller.concession_id);
    const availableBrands = concession?.brands || [];
    
    if (availableBrands.some(b => b.toUpperCase() === brandStr.toUpperCase())) {
      const exactMatch = availableBrands.find(b => b.toUpperCase() === brandStr.toUpperCase());
      return [exactMatch];
    }
    
    if (/\s+et\s+/i.test(brandStr)) {
      return brandStr.split(/\s+et\s+/i).map(b => b.trim()).filter(Boolean);
    }
    
    if (brandStr.includes(' - ')) {
      return brandStr.split(/\s+-\s+/).map(b => b.trim()).filter(Boolean);
    }
    
    if (brandStr.includes(' & ')) {
      return brandStr.split(/\s+&\s+/).map(b => b.trim()).filter(Boolean);
    }
    
    if (availableBrands.length > 0) {
      const foundBrands = [];
      let remaining = brandStr.toUpperCase();
      const sortedBrands = [...availableBrands].sort((a, b) => b.length - a.length);
      
      for (const brand of sortedBrands) {
        const brandUpper = brand.toUpperCase();
        if (remaining.includes(brandUpper)) {
          foundBrands.push(brand);
          remaining = remaining.replace(new RegExp(brandUpper, 'g'), ' ').trim();
        }
      }
      
      if (foundBrands.length > 0) {
        return foundBrands;
      }
    }
    
    const parts = brandStr.split(/\s+/).filter(Boolean);
    if (parts.length >= 2 && availableBrands.length > 0) {
      const matchedParts = parts.filter(part => 
        availableBrands.some(b => b.toUpperCase() === part.toUpperCase())
      ).map(part => 
        availableBrands.find(b => b.toUpperCase() === part.toUpperCase())
      );
      if (matchedParts.length > 0) {
        return matchedParts;
      }
    }
    
    return [brandStr];
  };

  // Filter and search logic
  const filteredSellers = useMemo(() => {
    let result = sellers;
    
    // Filter by concession
    if (filterConcession !== "all") {
      result = result.filter(s => s.concession_id === filterConcession);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(s => 
        s.name?.toLowerCase().includes(query) ||
        s.email?.toLowerCase().includes(query) ||
        (s.brands || []).some(b => b?.toLowerCase().includes(query)) ||
        s.brand?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [sellers, filterConcession, searchQuery]);

  // Pagination logic
  const totalPages = Math.ceil(filteredSellers.length / itemsPerPage);
  const paginatedSellers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredSellers.slice(start, start + itemsPerPage);
  }, [filteredSellers, currentPage, itemsPerPage]);

  const handleOpenDialog = (seller = null) => {
    if (seller) {
      setSelectedSeller(seller);
      setFormData({
        name: seller.name,
        email: seller.email,
        phone: seller.phone || "",
        concession_id: seller.concession_id || "",
        brands: parseBrands(seller),
        brand_distribution: seller.brand_distribution || []
      });
    } else {
      setSelectedSeller(null);
      setFormData({
        name: "",
        email: "",
        phone: "",
        concession_id: user?.role === "manager" ? user.concession_id : "",
        brands: [],
        brand_distribution: []
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Fleet sellers don't need brands (they can sell all brands)
    if (isFleetConcession()) {
      if (!formData.name || !formData.email || !formData.concession_id) {
        toast.error("Veuillez remplir tous les champs obligatoires (nom, email, concession)");
        return;
      }
    } else {
      if (!formData.name || !formData.email || !formData.concession_id || formData.brands.length === 0) {
        toast.error("Veuillez remplir tous les champs obligatoires (nom, email, concession, au moins une marque)");
        return;
      }
      if (formData.brands.length > 2) {
        toast.error("Un vendeur peut avoir maximum 2 marques d'affectation");
        return;
      }
    }

    setSaving(true);
    try {
      const saveData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        concession_id: formData.concession_id,
        brands: isFleetConcession() ? ["Toutes marques du groupe"] : formData.brands
      };
      
      if (selectedSeller) {
        await api.put(`/sellers/${selectedSeller.id}`, saveData);
        toast.success("Vendeur mis à jour");
      } else {
        await api.post("/sellers", saveData);
        toast.success("Vendeur créé");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/sellers/${selectedSeller.id}`);
      toast.success("Vendeur supprimé définitivement");
      setDeleteDialogOpen(false);
      setSelectedSeller(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const openTransferDialog = (seller) => {
    setSelectedSeller(seller);
    setTransferTarget("");
    setTransferMigrate(true);
    setTransferDialogOpen(true);
    // Fetch full list of concessions (lightweight endpoint, works for managers too)
    api.get("/concessions/list-all")
      .then(res => setAllConcessions(res.data || []))
      .catch(() => setAllConcessions(concessions));
  };

  const handleTransfer = async () => {
    if (!selectedSeller || !transferTarget) return;
    setTransferring(true);
    try {
      const res = await api.post(`/sellers/${selectedSeller.id}/transfer`, {
        new_concession_id: transferTarget,
        migrate_draft_objectives: transferMigrate,
      });
      const d = res.data || {};
      toast.success(
        `Vendeur transféré vers ${d.new_concession}. ${d.draft_objectives_migrated || 0} brouillon(s) migré(s), ${d.historical_objectives_kept || 0} historique(s) conservé(s).`
      );
      setTransferDialogOpen(false);
      setSelectedSeller(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du transfert");
    } finally {
      setTransferring(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filterConcession !== "all") {
        params.concession_id = filterConcession;
      }
      
      const response = await api.get("/export/sellers", {
        params,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendeurs_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export Excel téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const getConcessionName = (concessionId) => {
    const concession = concessions.find(c => c.id === concessionId);
    return concession?.name || "Non assigné";
  };

  if (loading) {
    return (
      <Layout title="Vendeurs">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={user?.role === "manager" ? "Mes vendeurs" : "Gestion des vendeurs"}>
      {/* Header with filters */}
      <div className="flex flex-col gap-4 mb-6">
        {/* Top row: Search and Add button */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1 w-full md:w-auto">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, email ou marque..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Button
                onClick={handleExport}
                variant="outline"
                className="border-green-500 text-green-600 hover:bg-green-50"
                disabled={exporting}
                data-testid="export-sellers-btn"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export Excel
              </Button>
            )}
            <Button
              onClick={() => handleOpenDialog()}
              className="btn-primary"
              data-testid="add-seller-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau vendeur
            </Button>
          </div>
        </div>
        
        {/* Second row: Filters and count */}
        <div className="flex items-center gap-4">
          <p className="text-gray-500">
            {filteredSellers.length} vendeur{filteredSellers.length > 1 ? 's' : ''}
            {searchQuery && ` trouvé${filteredSellers.length > 1 ? 's' : ''}`}
          </p>
          {user?.role === "admin" && (
            <Select value={filterConcession} onValueChange={setFilterConcession}>
              <SelectTrigger className="w-48" data-testid="filter-concession">
                <SelectValue placeholder="Toutes les concessions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les concessions</SelectItem>
                {concessions.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card-dashboard" data-testid="sellers-table">
        {paginatedSellers.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Marque</TableHead>
                  {user?.role === "admin" && <TableHead>Concession</TableHead>}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedSellers.map((seller) => {
                  const sellerConcession = concessions.find(c => c.id === seller.concession_id);
                  const isFleet = sellerConcession?.is_fleet === true;
                  
                  return (
                  <TableRow key={seller.id} className={isFleet ? "bg-blue-50/50" : ""}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {seller.name}
                        {isFleet && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-600 text-white">
                            FLEET
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        {seller.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(seller.brands || [seller.brand]).filter(Boolean).map((brand, idx) => (
                          <span key={idx} className={`px-2 py-1 text-xs font-medium rounded-full ${isFleet ? 'bg-blue-200 text-blue-900' : 'bg-blue-100 text-blue-800'}`}>
                            {brand}
                          </span>
                        ))}
                        {(!seller.brands || seller.brands.length === 0) && !seller.brand && (
                          <span className="text-gray-400">-</span>
                        )}
                        {isFleet && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            Toutes marques
                          </span>
                        )}
                      </div>
                    </TableCell>
                    {user?.role === "admin" && (
                      <TableCell>{getConcessionName(seller.concession_id)}</TableCell>
                    )}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenDialog(seller)}
                          data-testid={`edit-seller-${seller.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => openTransferDialog(seller)}
                          title="Transférer vers un autre site"
                          data-testid={`transfer-seller-${seller.id}`}
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedSeller(seller);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-seller-${seller.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t" data-testid="pagination">
                <div className="text-sm text-gray-500">
                  Page {currentPage} sur {totalPages} ({filteredSellers.length} résultats)
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    data-testid="pagination-first"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={currentPage === pageNum ? "bg-[#E30613] hover:bg-[#c00510]" : ""}
                        data-testid={`pagination-page-${pageNum}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    data-testid="pagination-next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    data-testid="pagination-last"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <Users className="empty-state-icon" />
            <p>{searchQuery ? "Aucun vendeur trouvé" : "Aucun vendeur"}</p>
            {!searchQuery && (
              <Button
                onClick={() => handleOpenDialog()}
                className="mt-4 btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un vendeur
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {selectedSeller ? "Modifier le vendeur" : "Nouveau vendeur"}
            </DialogTitle>
            <DialogDescription>
              {selectedSeller 
                ? "Modifiez les informations du vendeur"
                : "Créez un nouveau compte vendeur"
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-1">
            <div className="space-y-2">
              <Label htmlFor="name">Nom complet *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Jean Dupont"
                data-testid="seller-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="vendeur@verbaere.com"
                data-testid="seller-email-input"
              />
            </div>
            {/* Concession selector - admin: all concessions, manager with multiple: their concessions */}
            {(user?.role === "admin" || user?.role === "hr" || (user?.role === "manager" && user?.concession_ids?.length > 1)) && (
              <div className="space-y-2">
                <Label htmlFor="concession">Concession *</Label>
                <Select 
                  value={formData.concession_id} 
                  onValueChange={(value) => setFormData({ ...formData, concession_id: value, brands: [] })}
                >
                  <SelectTrigger data-testid="seller-concession-select">
                    <SelectValue placeholder="Sélectionner une concession" />
                  </SelectTrigger>
                  <SelectContent>
                    {(user?.role === "admin" || user?.role === "hr" 
                      ? concessions 
                      : concessions.filter(c => user?.concession_ids?.includes(c.id) || c.id === user?.concession_id)
                    ).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Brand selector - only for non-Fleet sellers */}
            {!isFleetConcession() && (
            <div className="space-y-2">
              <Label>
                Marque(s) affectée(s) * 
                <span className="text-xs text-gray-500 ml-1">(max 2)</span>
              </Label>
              {!formData.concession_id ? (
                <p className="text-sm text-gray-500 italic">Choisir d'abord une concession</p>
              ) : (
                <div className="space-y-2 p-3 border rounded-lg bg-gray-50 max-h-48 overflow-y-auto">
                  {getAvailableBrands().map((brand) => (
                    <div key={brand} className="flex items-center space-x-3">
                      <Checkbox
                        id={`brand-${brand}`}
                        checked={formData.brands.includes(brand)}
                        disabled={formData.brands.length >= 2 && !formData.brands.includes(brand)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            if (formData.brands.length < 2) {
                              setFormData({ ...formData, brands: [...formData.brands, brand] });
                            }
                          } else {
                            setFormData(prev => ({ 
                              ...prev, 
                              brands: prev.brands.filter(b => b !== brand),
                            }));
                          }
                        }}
                        data-testid={`brand-checkbox-${brand}`}
                      />
                      <label 
                        htmlFor={`brand-${brand}`}
                        className={`text-sm font-medium cursor-pointer ${formData.brands.includes(brand) ? 'text-blue-700' : 'text-gray-700'}`}
                      >
                        {brand}
                      </label>
                      {formData.brands.includes(brand) && (
                        <Check className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                  ))}
                  {getAvailableBrands().length === 0 && (
                    <p className="text-sm text-gray-500 italic">Aucune marque disponible pour cette concession</p>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500">
                70% des objectifs VN doivent être réalisés sur ces marques
              </p>
            </div>
            )}

            {/* Fleet info message */}
            {isFleetConcession() && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Vendeur Fleet</strong> : peut vendre toutes les marques du groupe. La répartition des volumes par marque se définit dans la page <strong>Objectifs</strong> lors de la fixation des objectifs trimestriels.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              className="btn-primary"
              disabled={saving}
              data-testid="save-seller-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                selectedSeller ? "Mettre à jour" : "Créer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce vendeur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera définitivement "{selectedSeller?.name}" de la liste des vendeurs. 
              <br /><br />
              <strong>Note :</strong> L'historique des objectifs de ce vendeur sera conservé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Seller Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-md" data-testid="transfer-seller-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Transférer le vendeur
            </DialogTitle>
            <DialogDescription>
              Déplacez <strong>{selectedSeller?.name}</strong> depuis <strong>{(allConcessions.length > 0 ? allConcessions : concessions).find(c => c.id === selectedSeller?.concession_id)?.name || "?"}</strong> vers un autre site.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="transfer-target">Site de destination</Label>
              <Select value={transferTarget} onValueChange={setTransferTarget}>
                <SelectTrigger data-testid="transfer-target-select">
                  <SelectValue placeholder="Choisir un site..." />
                </SelectTrigger>
                <SelectContent>
                  {(allConcessions.length > 0 ? allConcessions : concessions)
                    .filter(c => c.id !== selectedSeller?.concession_id)
                    .map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <Checkbox
                id="transfer-migrate"
                checked={transferMigrate}
                onCheckedChange={setTransferMigrate}
                data-testid="transfer-migrate-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="transfer-migrate" className="cursor-pointer font-medium">
                  Migrer les objectifs brouillons
                </Label>
                <p className="text-xs text-gray-600 mt-0.5">
                  Les objectifs en <strong>brouillon</strong> passeront sur le nouveau site.
                  Les objectifs <strong>signés ou en attente de signature</strong> restent liés à l'ancien site pour préserver l'historique.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)} disabled={transferring}>
              Annuler
            </Button>
            <Button
              onClick={handleTransfer}
              disabled={!transferTarget || transferring}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="transfer-confirm-btn"
            >
              {transferring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Transfert...
                </>
              ) : (
                "Confirmer le transfert"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
